import axios from 'axios';

/**
 * Pure helpers for the Batches sub-feature (Mail-on-Demand print-shop
 * admin). Deliberately free of React and of `Batches/api.ts` so they stay
 * unit-testable in isolation and importable from either the builder page or
 * the sibling `printShop/TicketDetail.tsx` without pulling in the API
 * client.
 */

/**
 * The `order_in_open_batch` 409 conflict body shape returned by
 * `POST /v1/abo/mod/tickets/:orderId/ship` when the order already sits in a
 * different open batch.
 */
interface OrderInOpenBatchErrorBody {
  error?: string;
  batchId?: string;
}

/** Result shape returned by `getBatchConflictInfo` on a genuine conflict. */
export interface BatchConflictInfo {
  batchId: string;
}

const ORDER_IN_OPEN_BATCH = 'order_in_open_batch';

/**
 * Detects the `order_in_open_batch` 409 conflict the ship mutation in
 * `TicketDetail.tsx` (task_06) branches on. Mirrors `getApiErrorMessage`'s
 * `axios.isAxiosError` narrowing pattern in `printShop/helpers.ts`, but is
 * intentionally its own function: this is the single source of truth that
 * decides whether the distinct "already in a batch" banner/link renders, so
 * every shape below MUST resolve to `null` and only the genuine conflict
 * resolves to a `{ batchId }` result:
 *   - not an axios error at all (network error, plain `Error`, non-Error
 *     thrown value)
 *   - an axios error with any status other than 409 (404, 500, ...)
 *   - a 409 whose body's `error` field isn't exactly `'order_in_open_batch'`
 *   - a 409 with the right `error` field but a missing or blank `batchId`
 */
export const getBatchConflictInfo = (
  error: unknown,
): BatchConflictInfo | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  if (error.response?.status !== 409) {
    return null;
  }

  const body = error.response.data as OrderInOpenBatchErrorBody | undefined;
  if (body?.error !== ORDER_IN_OPEN_BATCH) {
    return null;
  }

  const batchId = body.batchId;
  if (typeof batchId !== 'string' || batchId.trim().length === 0) {
    return null;
  }

  return { batchId };
};

/**
 * Display form of a batch id, matching the mockup's `BATCH-7F2A91C3` chip.
 * Shared by the builder header and the batches LIST page (both must show the
 * same short form for a given batch — only the underlying route/link target
 * uses the real id).
 */
export const batchDisplayId = (batchId: string): string =>
  `BATCH-${(batchId.length > 8 ? batchId.slice(-8) : batchId).toUpperCase()}`;

/** Colors the builder's progress-bar uses for the package-count indicator. */
export type PackageProgressColor = 'amber' | 'green';

/**
 * Progress-bar color for the builder's package-count indicator (mockup's
 * soft-target visual treatment): `'green'` once the batch has reached (or
 * passed) its target package count, `'amber'` while it's still under
 * target.
 */
export const packageProgressColor = (
  count: number,
  target: number,
): PackageProgressColor => (count >= target ? 'green' : 'amber');

/**
 * Re-exported (not duplicated) from `printShop/helpers.ts` so
 * `BatchBuilder.tsx` (task_05) can download the batch manifest CSV through
 * the same blob-download utility the rest of `printShop/` already uses.
 */
export { downloadBlob } from '../helpers';
